import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import type { Difficulty, ChallengeType } from '../../utils/mathProblems';

const DIFFICULTIES: { value: Difficulty; label: string; desc: string; color: string; bg: string }[] = [
  { value: 'easy', label: 'Easy', desc: 'Simple problems, no timer. Perfect for warm-ups.', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  { value: 'medium', label: 'Medium', desc: 'More challenging numbers, still no timer.', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' },
  { value: 'hard', label: 'Hard', desc: 'Complex problems with a 20-second timer per question.', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' },
  { value: 'expert', label: 'Expert', desc: 'Word problems, only 12 seconds — every second counts!', color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
];

const CHALLENGES: { value: ChallengeType; label: string; icon: string; desc: string }[] = [
  { value: 'multiplication', label: 'Multiplication', icon: 'ri-close-line', desc: 'Products & word problems' },
  { value: 'volume', label: 'Volume', icon: 'ri-box-3-line', desc: '3D shape calculations' },
  { value: 'division', label: 'Division', icon: 'ri-divide-line', desc: 'Quotients, remainders & decimals' },
];

const PATH_MAP: Record<ChallengeType, string> = {
  multiplication: '/multiplication-challenge',
  volume: '/volume-challenge',
  division: '/division-challenge',
};

export default function LevelSelector() {
  const [selectedDifficulty, setSelectedDifficulty] = useState<Difficulty | null>(null);
  const [selectedChallenge, setSelectedChallenge] = useState<ChallengeType | null>(null);
  const navigate = useNavigate();

  const canStart = selectedDifficulty && selectedChallenge;

  const handleStart = () => {
    if (!canStart) return;
    navigate(`${PATH_MAP[selectedChallenge]}?level=${selectedDifficulty}`);
  };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      <section className="relative pt-28 pb-10 px-6 bg-green-900 text-white text-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/b863ac20dbb812e2c61ed36d2db228e7.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative">
          <span className="text-xs font-bold uppercase tracking-widest text-lime-400 bg-lime-400/10 border border-lime-400/30 px-3 py-1 rounded-full">
            Level Selector
          </span>
          <h1 className="text-5xl font-bold mt-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
            CHOOSE YOUR MATCH
          </h1>
          <p className="text-green-300 mt-2 text-sm">Select a challenge type and difficulty to begin your match.</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-12">
        {/* Step 1 */}
        <div className="mb-10">
          <h2 className="flex items-center gap-2 text-lg font-bold text-green-900 mb-4">
            <span className="w-7 h-7 flex items-center justify-center bg-green-800 text-white text-xs font-bold rounded-full">1</span>
            Choose Challenge Type
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {CHALLENGES.map(ch => (
              <button
                key={ch.value}
                onClick={() => setSelectedChallenge(ch.value)}
                className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all cursor-pointer ${
                  selectedChallenge === ch.value
                    ? 'border-green-700 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-green-300'
                }`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-full ${selectedChallenge === ch.value ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <i className={`${ch.icon} text-2xl`} />
                </div>
                <div>
                  <div className="font-bold text-green-900">{ch.label}</div>
                  <div className="text-xs text-gray-500">{ch.desc}</div>
                </div>
                {selectedChallenge === ch.value && (
                  <i className="ri-checkbox-circle-fill text-green-600 text-lg" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Step 2 */}
        <div className="mb-10">
          <h2 className="flex items-center gap-2 text-lg font-bold text-green-900 mb-4">
            <span className="w-7 h-7 flex items-center justify-center bg-green-800 text-white text-xs font-bold rounded-full">2</span>
            Choose Difficulty
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {DIFFICULTIES.map(d => (
              <button
                key={d.value}
                onClick={() => setSelectedDifficulty(d.value)}
                className={`relative flex flex-col gap-2 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  selectedDifficulty === d.value
                    ? `${d.bg} border-current`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                } ${d.color}`}
              >
                {selectedDifficulty === d.value && (
                  <i className="ri-checkbox-circle-fill absolute top-3 right-3 text-base" />
                )}
                <span className="font-bold text-base">{d.label}</span>
                <span className="text-xs text-gray-500 leading-relaxed">{d.desc}</span>
                {(d.value === 'hard' || d.value === 'expert') && (
                  <span className="text-xs font-semibold flex items-center gap-1">
                    <i className="ri-time-line" />
                    {d.value === 'hard' ? '20s timer' : '12s timer'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Summary & Start */}
        {canStart && (
          <div className="bg-green-900 rounded-2xl p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-green-300 text-sm mb-1">Your match setup:</p>
              <p className="text-xl font-bold capitalize">
                {selectedChallenge} Challenge — {selectedDifficulty}
              </p>
              <p className="text-green-400 text-xs mt-1">Best of 3 sets · Official tennis scoring</p>
            </div>
            <button
              onClick={handleStart}
              className="px-8 py-3 rounded-full bg-lime-400 text-green-900 font-bold text-base hover:bg-lime-300 cursor-pointer whitespace-nowrap transition-all"
            >
              Start Match
              <i className="ri-play-fill ml-2" />
            </button>
          </div>
        )}

        {!canStart && (
          <div className="text-center py-6 text-gray-400 text-sm">
            <i className="ri-arrow-up-line mr-1" />
            Select both a challenge type and difficulty to begin
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
